<template>
	<v-dialog
		:model-value="modelValue"
		@update:model-value="$emit('update:modelValue', $event)"
		width="960"
		max-height="900"
		min-height="400"
		transition="dialog-bottom-transition"
		persistent
	>
		<v-card class="pa-0">
			<v-card-title class="d-flex justify-space-between align-center">
				<div>
					<span class="duty-title mr-3">{{ dialogTitle }}</span>
					<span class="duty-subtitle mr-2">{{ formatDate(useDayStore().daysRef[localDuty.day]) }}</span>
					<v-chip density="compact" class="mr-2" v-if="localDuty.modified">
						<v-icon class="planning-table-chip-icon-modified" color="info-light">mdi-checkbox-blank-circle</v-icon>
						{{ $t('planning_duty_modified') }}
					</v-chip>
					<v-chip class="mr-2" density="compact" v-if="localDuty.exchange">
						<v-icon class="mr-1">mdi-swap-vertical</v-icon>
						{{ $t('duty_in_exchange') }}
					</v-chip>
				</div>

				<div class="d-flex align-center">
					<v-menu v-if="!isMenuLocked && (!isPastOrPublishedDay || !isActive)" location="bottom end">
						<template #activator="{ props }">
							<v-icon v-bind="props" class="duty-action-icon"> mdi-dots-vertical </v-icon>
						</template>

						<v-list class="duty-action-menu">
							<v-list-item
								v-if="isDutyType"
								class="duty-action-item"
								:disabled="uiStore.hasPendingChangesInDutyModal"
								@click="handleDutyAction('split')"
							>
								<div class="duty-action-item__content">
									<span class="duty-action-item__icon">
										<v-img contain :src="splitSVG" alt="" width="16" height="16" />
									</span>
									<span class="duty-action-item__title">{{ $t('action_split_duty') }}</span>
								</div>
							</v-list-item>

							<v-list-item
								v-if="isDutyType"
								class="duty-action-item"
								:disabled="uiStore.hasPendingChangesInDutyModal"
								@click="handleDutyAction('merge')"
							>
								<div class="duty-action-item__content">
									<span class="duty-action-item__icon">
										<v-img contain :src="mergeSVG" alt="" width="16" height="16" />
									</span>
									<span class="duty-action-item__title">{{ $t('action_merge_duty') }}</span>
								</div>
							</v-list-item>

							<v-divider v-if="isDutyType" />

							<v-list-item class="duty-action-item duty-action-item--delete" @click="handleDutyAction('delete')">
								<div class="duty-action-item__content">
									<span class="duty-action-item__icon">
										<v-icon size="16">mdi-delete</v-icon>
									</span>
									<span class="duty-action-item__title">{{ $t('duty_delete') }}</span>
								</div>
							</v-list-item>
						</v-list>
					</v-menu>
					<v-icon class="duty-action-icon" @click="closeDialog">mdi-close</v-icon>
				</div>
			</v-card-title>

			<v-tabs class="tabs" color="primary" background-color="transparent" :model-value="activeTab" @update:model-value="handleTabChange">
				<v-tab v-for="tab in tabs" :key="tab.value" :value="tab.value">
					{{ $t(tab.value) }}
				</v-tab>
			</v-tabs>

			<v-card-text>
				<div class="tab-content-wrapper">
					<component
						:is="getCurrentTabComponent()"
						:key="tabKeys[activeTab]"
						:dutyID="getDutyIdForTab()"
						:userID="localDuty.user"
						:dayID="localDuty.day"
						:fields-to-disable="[]"
						:custom-duty="isCustom ? localDuty : null"
						@close="closeDialog"
						@handle-tab-change="setPendingTab"
						@duty-updated="handleDutyUpdated"
					/>
				</div>
			</v-card-text>

			<v-divider></v-divider>

			<v-card-actions v-if="activeTab != 'planning_disposition' && activeTab != 'planning_duty_details'">
				<v-spacer></v-spacer>
				<v-btn variant="text" @click="$emit('update:modelValue', false)">{{ $t('action_close') }}</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<SplitDutyDialog
		v-if="props.duty"
		:model-value="showSplitDialog"
		@update:model-value="showSplitDialog = $event"
		:dutyId="props.duty.id"
		:breaks="storeDuty?.breaks"
		:if-comment="ifComment"
		:loading="isSplitting"
		@split="handleSplitDuty"
	/>
	<MergeDutyDialog
		v-if="props.duty"
		:model-value="showMergeDialog"
		@update:model-value="showMergeDialog = $event"
		:dutyId="props.duty.id"
		:date="storeDuty?.dayObject?.dateString"
		:if-comment="ifComment"
		:loading="isMerging"
		@merge="handleMergeDuty"
	/>
	<DeleteDutyDialog
		v-if="props.duty"
		:model-value="showDeleteDialog"
		@update:model-value="showDeleteDialog = $event"
		:duty-id="props.duty.id"
		:if-comment="ifComment"
		:loading="isDeleting"
		@delete="handleDeleteDuty"
	/>
</template>

<script setup lang="ts">
import { ref, watch, reactive, computed, defineAsyncComponent, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUIStore } from '@/rennovation/planning-view/store';
import { useDutyStore } from '@/rennovation/planning-view/store/useDutyStore';
import { PLANNING_DIALOG_TABS } from '../../../constants';
import PlanningDialogDispositionTab from './PlanningDialogDispositionTab.vue';
import PlanningDialogDutyActivitiesTab from './DutyActivties/PlanningDialogDutyActivitiesTab.vue';
import PlanningDutyReports from '@/rennovation/planning-view/components/Layout/Modals/PlanningDutyReports.vue';
import PlanningHistoryTab from './PlanningHistoryTab.vue';
import { DutyItemResponse } from '../../../types/models/duty';
import PlanningDialogDetailsTab from './PlanningDialogDetailsTab.vue';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';
import { useDateTime } from '../../../../../../components/composables/useDateTime';
import splitSVG from '@/assets/split.svg';
import mergeSVG from '@/assets/merge.svg';
import { DutyBreak, ApiDuty } from '@/rennovation/planning-view/types/api/duties-response';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';
import metricsService from '@/services/metricsService.js';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';

const SplitDutyDialog = defineAsyncComponent(() => import('@/rennovation/planning-view/components/Layout/Modals/SplitDutyDialog.vue'));
const MergeDutyDialog = defineAsyncComponent(() => import('@/rennovation/planning-view/components/Layout/Modals/MergeDutyDialog.vue'));
const DeleteDutyDialog = defineAsyncComponent(() => import('@/rennovation/planning-view/components/Layout/Modals/DeleteDutyDialog.vue'));

const showSplitDialog = ref(false);
const showMergeDialog = ref(false);
const showDeleteDialog = ref(false);

const isSplitting = ref(false);
const isMerging = ref(false);
const isDeleting = ref(false);

const showError = (error: Error, fallback = 'Something went wrong') => {
	const message = (error as any)?.response?.data?.message || error.message || fallback;

	alertStore.showAlert(message, 'error');
};

const showSuccess = (message: string) => {
	alertStore.showAlert(message, 'success');
};

const props = defineProps<{
	modelValue: boolean;
	duty: DutyItemResponse;
	isCustom: boolean;
}>();

const emit = defineEmits(['update:modelValue', 'update:duty', 'duty-updated', 'close']);

const localDuty = ref<DutyItemResponse>({ ...props.duty });

watch(
	() => props.duty,
	(newDuty) => {
		localDuty.value = { ...newDuty };
	},
	{ deep: true }
);

const uiStore = useUIStore();
const dutyStore = useDutyStore();
const { t } = useI18n();
const { formatDate } = useDateTime();
const alertStore = useAlertStore();
const dayStore = useDayStore();

const { isActive } = useDutyChangePreview();
const dayDate = computed(() => storeDuty.value?.dayObject?.dateString ?? dayStore.daysRef[localDuty.value.day]);
const isPastOrPublishedDay = computed(() => dayStore.isPastOrPublishedDay(dayDate.value));
const activitiesDutyId = computed(() => (isActive.value ? localDuty.value?.original : localDuty.value.id));
const getDutyIdForTab = () => (['planning_duty_activities', 'planning_history'].includes(activeTab.value) ? activitiesDutyId.value : localDuty.value.id);

const tabs = [
	{ value: 'planning_disposition', name: PLANNING_DIALOG_TABS.DISPOSITION },
	{ value: 'planning_duty_details', name: PLANNING_DIALOG_TABS.DUTY_DETAILS },
	{ value: 'planning_duty_activities', name: PLANNING_DIALOG_TABS.DUTY_ACTIVITIES },
	{ value: 'planning_duty_reports', name: PLANNING_DIALOG_TABS.REPORTS },
	{ value: 'planning_history', name: PLANNING_DIALOG_TABS.HISTORY },
];

const activeTab = ref('planning_disposition');
const pendingTab = ref('');

// Track keys for each tab to force remount when revisited
const tabKeys = reactive<Record<string, number>>({
	planning_disposition: 0,
	planning_history: 0,
	planning_duty_details: 0,
	planning_duty_reports: 0,
	planning_duty_activities: 0,
});

// ============================================
// FUNCTIONS / ACTIONS
// ============================================
const storeDuty = computed<ApiDuty | undefined>(() => dutyStore.dutiesById[props.duty.id]);
const dateRangeStatus = computed(() => dayStore.getDateRangeStatus(storeDuty.value?.dayObject?.dateString ?? ''));

const isMenuLocked = computed(() => {
	const isAbsenceFixedDuty = storeDuty.value?.fixedBy === 'absence';

	return dateRangeStatus.value === 'locked' || isAbsenceFixedDuty;
});

const ifComment = computed(() => dateRangeStatus.value === 'past' || dateRangeStatus.value === 'published');

const isDutyType = computed(() => props.duty.type === 'duty');

const dialogTitle = computed(() => {
	let title = '';
	const dutyName = localDuty.value.nameKey ? t(localDuty.value.nameKey) : localDuty.value.name;
	switch (localDuty.value.type) {
		case 'rest':
			if (localDuty.value.isRestSunday) {
				title = t('duty_dayType_restSunday');
			} else {
				title = t('duty_dayType_rest');
			}
			break;
		case 'compensation':
			title = t('duty_dayType_compensation');
			break;
		case 'vacation':
			title = t('duty_dayType_vacation');
			break;
		case 'absence':
			title = t('duty_dayType_absence') + ' ' + dutyName;
			break;
		case 'reserve':
			title = t('duty_dayType_reserve');
			break;
		default:
			title = t('duty_duty') + ' ' + dutyName;
	}
	return title;
});

const getCurrentTabComponent = () => {
	switch (activeTab.value) {
		case 'planning_disposition':
			return PlanningDialogDispositionTab;
		case 'planning_duty_details':
			return PlanningDialogDetailsTab;
		case 'planning_duty_activities':
			return PlanningDialogDutyActivitiesTab;
		case 'planning_history':
			return PlanningHistoryTab;
		case 'planning_duty_reports':
			return PlanningDutyReports;
		default:
			return null;
	}
};

const handleDeleteDuty = async (payload: { dutyId: string; comment: string }) => {
	isDeleting.value = true;
	try {
		await dutyStore.deleteDuty({
			dutyId: payload.dutyId,
			changeComment: payload.comment,
		});

		showDeleteDialog.value = false;
		closeDialog();
		showSuccess(t('duty_delete_success'));
	} catch (error) {
		showError(error, t('duty_delete_failed'));
	} finally {
		isDeleting.value = false;
	}
};

const handleMergeDuty = async (payload: { dutyId: string; mergeWithId: string; comment: string }) => {
	isMerging.value = true;
	try {
		await dutyStore.mergeDuty({
			dutyId: payload.dutyId,
			mergeWithId: payload.mergeWithId,
			changeComment: payload.comment,
		});

		showMergeDialog.value = false;

		emit('update:modelValue', false);
		showSuccess(t('duty_merge_success'));
	} catch (error) {
		showError(error, t('duty_merge_failed'));
	} finally {
		isMerging.value = false;
	}
};

const handleSplitDuty = async ({ id, splitBreak, changeComment }: { id: string; splitBreak: DutyBreak; changeComment: string }) => {
	isSplitting.value = true;
	try {
		await dutyStore.splitDuty({
			dutyId: id,
			splitBreak,
			changeComment,
		});

		showSplitDialog.value = false;

		emit('update:modelValue', false);
		showSuccess(t('duty_split_success'));
	} catch (error) {
		showError(error, t('duty_split_failed'));
	} finally {
		isSplitting.value = false;
	}
};

type DutyAction = 'split' | 'merge' | 'delete';

const handleDutyAction = (action: DutyAction) => {
	switch (action) {
		case 'split':
			showSplitDialog.value = true;
			break;

		case 'merge':
			showMergeDialog.value = true;
			break;

		case 'delete':
			showDeleteDialog.value = true;
			break;
		default:
			break;
	}
};

const closeDialog = () => {
	emit('update:modelValue', false);
};

const handleTabChange = (nextTab: string) => {
	uiStore.tabClicked = true;
	if (uiStore.hasPendingChangesInDutyModal) {
		console.warn('Navigation blocked: Pending changes detected.');
		pendingTab.value = nextTab;
		return;
	}

	if (tabKeys[nextTab] !== undefined) {
		tabKeys[nextTab] += 1;
	}

	activeTab.value = nextTab;
};

const setPendingTab = () => {
	activeTab.value = pendingTab.value;
	uiStore.setHasPendingChanges(false);
};

const handleDutyUpdated = (payload: Partial<DutyItemResponse>) => {
	localDuty.value = {
		...localDuty.value,
		...payload,
	};

	emit('update:duty', payload);
};

watch(
	() => props.modelValue,
	async (isOpen) => {
		if (isOpen) {
			metricsService.startTracking('PlanningDutyDialog-Open');
			activeTab.value = 'planning_disposition';

			Object.keys(tabKeys).forEach((key) => {
				tabKeys[key] = 0;
			});
			uiStore.setHasPendingChanges(false);

			if (!dutyStore.dutiesById[props.duty.id]) {
				await dutyStore.fetchDutyByID(props.duty.id);
			}

			nextTick(() => {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						metricsService.endTracking('PlanningDutyDialog-Open', 'PlanningDutyDialog.vue');
					});
				});
			});
		}
	}
);
</script>

<style scoped>
.duty-title {
	font-family: Roboto;
	font-weight: 400;
	font-size: 20px;
	line-height: 32px;
	letter-spacing: 0.25px;
	vertical-align: middle;
}
.duty-subtitle {
	font-family: Roboto;
	font-weight: 400;
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.25px;
	vertical-align: middle;
}

:deep(.v-dialog > .v-overlay__content > .v-card > .v-card-text),
:deep(.v-dialog > .v-overlay__content > form > .v-card > .v-card-text) {
	padding: 0 !important;
}

:deep(.v-card-text) {
	padding: 0 !important;
}

.planning-table-chip-icon-modified {
	font-size: 8px;
	margin-right: 6px;
}
.duty-action-icon {
	font-size: 24px;
	opacity: 1;
	color: #959191;
}

.duty-action-menu {
	min-width: 180px;
	height: 144px;
	padding: 0;
}

.duty-action-item {
	height: 48px;
	min-height: 48px;
	padding: 0 16px;
}

.duty-action-item__content {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	width: 100%;
}

.duty-action-item__icon {
	flex-shrink: 0;
	flex-grow: 0;
	margin-right: 8px;
	width: 16px;
	height: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.duty-action-item__title {
	font-family: 'Inter', 'Roboto', sans-serif;
	font-weight: 400;
	font-size: 16px;
	line-height: 24px;
	letter-spacing: 0%;
	text-align: center;
	vertical-align: middle;
	color: #000000de;
}

.duty-action-item--delete {
	color: #ff5252;
}

.duty-action-item--delete .duty-action-item__icon,
.duty-action-item--delete .duty-action-item__title {
	color: #ff5252;
}
</style>
